import React, { useState, useEffect, useContext } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import envConfig from '../envConfig';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';

function Filters({ setAdvisoryData, setSelectedFilters }) {
    const { isAuthenticated, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const [selectedCities, setSelectedCities] = useState(null);
    const cities = [{ name: 'Zambia', code: 'ZM' }]; // Hardcoded as per original
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [villages, setVillages] = useState([]);
    const [species, setSpecies] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [selectedVillage, setSelectedVillage] = useState(null);
    const [selectedSpecies, setSelectedSpecies] = useState(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('AM');
    const [selectedHour, setSelectedHour] = useState('');
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [formError, setFormError] = useState(null);

    // Function to get auth headers   
    const getAuthHeaders = () => {
        const token = localStorage.getItem('profishBlue_authToken');
        const expiryTime = localStorage.getItem('profishBlue_tokenExpiry');

        const isTokenExpired = !token || !expiryTime || Date.now() > parseInt(expiryTime, 10);

        if (isTokenExpired) {
            logout();
            window.location.href = '/login';
            return {};
        }

        return {
            'Content-Type': 'application/json',
            Authorization: token,
        };
    };

    // Handle API errors
    const handleApiError = (error, response) => {
        if (response && response.status === 401) {
            logout();
            window.location.href = '/login';
            return 'Session expired. Please log in again.';
        }
        return error.message || 'An error occurred. Please try again.';
    };

    // Fetch countries
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${envConfig.API_URL}/lookups/locations/country`, {
                    headers: getAuthHeaders(),
                });
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setCountries(data.data || []);
                const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
                if (+userDetails?.role_id === 1) {
                    setSelectedCountry(data?.data?.[1]?.country_id || null);
                } else {
                    setSelectedCountry(data?.data?.[0]?.country_id || null);
                }
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        fetchCountries();
    }, []);

    // Fetch provinces based on selected country
    useEffect(() => {
        const fetchProvinces = async () => {
            if (!selectedCountry) return;
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`,
                    {
                        headers: getAuthHeaders(),
                    }
                );
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setProvinces(data.data || []);
                setSelectedProvince(null); // Reset province when country changes
                setDistricts([]); // Clear districts
                setVillages([]); // Clear villages
                setSelectedDistrict(null); // Reset district
                setSelectedVillage(null); // Reset village
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        fetchProvinces();
    }, [selectedCountry]);

    // Fetch districts based on selected country and province
    useEffect(() => {
        const fetchDistricts = async () => {
            if (!selectedCountry || !selectedProvince) return;
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}&adm1_id=${selectedProvince}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setDistricts(data.data || []);
                setSelectedDistrict(null); // Reset district when province changes
                setVillages([]); // Clear villages
                setSelectedVillage(null); // Reset village
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        fetchDistricts();
    }, [selectedProvince]);

    // Fetch villages based on selected country, province, and district
    useEffect(() => {
        const fetchVillages = async () => {
            if (!selectedCountry || !selectedProvince || !selectedDistrict) return;
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}&adm1_id=${selectedProvince}&adm2_id=${selectedDistrict}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setVillages(data.data || []);
                setSelectedVillage(null); // Reset village when district changes
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        fetchVillages();
    }, [selectedDistrict]);

    // Fetch species based on selected country
    useEffect(() => {
        const fetchSpecies = async () => {
            if (!selectedCountry) return;
            try {
                const response = await fetch(
                    `${envConfig.API_URL}/lookups/fishes/species?country_id=${selectedCountry}`,
                    { headers: getAuthHeaders() }
                );
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`);
                }
                const data = await response.json();
                setSpecies(data.data || []);
                setSelectedSpecies(null); // Reset species when country changes
            } catch (error) {
                setFormError(handleApiError(error, error.response));
            }
        };

        fetchSpecies();
    }, [selectedCountry]);

    // Handle country change
    const onCountryChange = (e) => {
        setSelectedCountry(e.target.value);
    };

    // Handle species change
    const speciesChange = (e) => {
        const selected = species.find((s) => s.species_id === parseInt(e.target.value));
        setSelectedSpecies(selected || null);
    };

    // Handle form submission
    const handleSubmit = async () => {
        const validations = [
            { condition: !selectedProvince, message: 'Please select Province' },
            { condition: !selectedDistrict, message: 'Please select District' },
            { condition: !selectedVillage, message: `Please select ${selectedCountry == 1 ? 'EPA' : 'Camp'}` },
            { condition: !selectedSpecies, message: 'Please select Species' },
            { condition: !selectedDate, message: 'Please select Date' },
        ];

        if (selectedCountry == '1') {
            validations.push({ condition: !selectedHour, message: 'Please select time' });
        }

        for (let v of validations) {
            if (v.condition) {
                Swal.fire({
                    title: 'Selection Required',
                    text: v.message,
                    icon: 'warning',
                    confirmButtonText: 'OK',
                });
                return;
            }
        }

        const token = localStorage.getItem('profishBlue_authToken');
        const profishBlue_tokenExpiry = localStorage.getItem('profishBlue_tokenExpiry');

        if (!token || !profishBlue_tokenExpiry || Date.now() > parseInt(profishBlue_tokenExpiry)) {
            logout();
            Swal.fire({
                title: 'Authentication Error',
                text: 'Session expired. Please log in again.',
                icon: 'error',
                confirmButtonText: 'OK',
            }).then(() => {
                window.location.href = '/login';
            });
            return;
        }

        const dayValue = selectedTime === 'AM' ? 1 : 0;
        const payload = {
            "country_id": parseInt(selectedCountry),
            "day": dayValue,
            "hours": parseInt(selectedHour),
            "adm3_id": parseInt(selectedVillage),
            "date": selectedDate,
            "species_code": selectedSpecies?.code
        };

        try {
            const response = await fetch(`${envConfig.API_URL}/advisory/pond_advisory`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { status: response.status, ...errorData };
            }

            const result = await response.json();

            const selectedData = {
                country: countries.find((c) => c.country_id === selectedCountry)?.country || 'Zambia',
                province:
                    provinces.find((item) => item.adm1_id === parseInt(selectedProvince))?.adm1 || 'NA',
                district:
                    districts.find((item) => item.adm2_id === parseInt(selectedDistrict))?.adm2 || 'NA',
                village: villages.find((item) => item.adm3_id === parseInt(selectedVillage))?.adm3 || 'NA',
                selectedSpecies: selectedSpecies || null,
                date: selectedDate || 'NA',
                day: dayValue || 'NA',
                "country_id": selectedCountry,
                "adm3_id": selectedVillage,
                "adm2_id": selectedDistrict,
                "adm1_id": selectedProvince,
                "predicted_pond_temperature": result.data?.predicted_pond_temperature || 'NA',
                "temp_max": result.data?.temp_max || 'NA',
                "temp_min": result.data?.temp_min || 'NA',
                "advisory_text": result.data?.advisory_message || 'NA',
                "ref_date": selectedDate || 'NA',
                "risk_level": result.data?.risk_level || 'NA',
                forecastMaxTemp: parseFloat((result.data?.nw_forecast_temp?.reduce((sum, day) => sum + day.temp_max, 0) / result.data.nw_forecast_temp.length).toFixed(1)),
                forecastMinTemp: parseFloat((result.data?.nw_forecast_temp?.reduce((sum, day) => sum + day.temp_min, 0) / result.data.nw_forecast_temp.length).toFixed(1)),
                histoircMaxTemp: parseFloat((result.data?.lw_observed_temp?.reduce((sum, day) => sum + day.temp_max, 0) / result.data.lw_observed_temp.length).toFixed(1)),
                histoircMinTemp: parseFloat((result.data?.lw_observed_temp?.reduce((sum, day) => sum + day.temp_min, 0) / result.data.lw_observed_temp.length).toFixed(1)),
            };

            setSelectedFilters(selectedData);
            setAdvisoryData(result.data);
        } catch (error) {
            const message =
                error?.message ||
                    error?.message === '' ||
                    error?.status === 404
                    ? error?.message || 'Weather data is absent for the selection.'
                    : 'Error submitting request.';

            Swal.fire({
                title: 'Error',
                text: message,
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    return (
        <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-12">
                <div className="filter-box pt-3">
                    <h5>Selection</h5>
                    <div className="form-group">
                        <label htmlFor="country">Country <span className="text-danger">*</span></label>
                        <select
                            className="form-select custom-select"
                            value={selectedCountry || ''}
                            onChange={onCountryChange}
                        >
                            {countries.map((country) => (
                                <option key={country.country_id} value={country.country_id}>
                                    {country.country}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="province">Province <span className="text-danger">*</span></label>
                        <select
                            className="form-select custom-select"
                            value={selectedProvince || ''}
                            onChange={(e) => setSelectedProvince(e.target.value)}
                            disabled={!selectedCountry}
                        >
                            <option value="">Select</option>
                            {provinces.map((province) => (
                                <option key={province.adm1_id} value={province.adm1_id}>
                                    {province.adm1}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="district">District <span className="text-danger">*</span></label>
                        <select
                            className="form-select custom-select"
                            value={selectedDistrict || ''}
                            onChange={(e) => setSelectedDistrict(e.target.value)}
                            disabled={!selectedProvince}
                        >
                            <option value="">Select</option>
                            {districts.map((district) => (
                                <option key={district.adm2_id} value={district.adm2_id}>
                                    {district.adm2}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="village">{selectedCountry == 1 ? 'EPA' : 'Camp'} <span className="text-danger">*</span></label>
                        <select
                            className="form-select custom-select"
                            value={selectedVillage || ''}
                            onChange={(e) => setSelectedVillage(e.target.value)}
                            disabled={!selectedDistrict}
                        >
                            <option value="">Select</option>
                            {villages.map((village) => (
                                <option key={village.adm3_id} value={village.adm3_id}>
                                    {village.adm3}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="species">Species <span className="text-danger">*</span></label>
                        <select
                            className="form-select custom-select"
                            value={selectedSpecies?.species_id || ''}
                            onChange={speciesChange}
                            disabled={!selectedCountry} // Enable only when country is selected
                        >
                            <option value="">Select</option>
                            {species.map((sp) => (
                                <option key={sp.species_id} value={sp.species_id}>
                                    {sp.species}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label htmlFor="date">Date <span className="text-danger">*</span></label>
                        <input
                            type="date"
                            className="form-control"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            max={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]} // Set max to 14 days from today
                        />
                    </div>
                    {
                        selectedCountry == '1' && (
                            <div className="form-group">
                                <label htmlFor="hour">Hour <span className="text-danger">*</span></label>
                                <select
                                    className="form-control"
                                    value={selectedHour}
                                    onChange={(e) => setSelectedHour(e.target.value)}
                                >
                                    <option value="">Select Hour</option>
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {i}:00
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )
                    }

                    {
                        selectedCountry == '2' && (
                            <div className="form-group">
                                <label>Time *</label>
                                <div className="d-flex gap-3">
                                    <div className="form-check">
                                        <input
                                            type="radio"
                                            id="am"
                                            name="time"
                                            className="form-check-input"
                                            value="AM"
                                            checked={selectedTime === 'AM'}
                                            onChange={(e) => setSelectedTime(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="am">
                                            AM
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            type="radio"
                                            id="pm"
                                            name="time"
                                            className="form-check-input"
                                            value="PM"
                                            checked={selectedTime === 'PM'}
                                            onChange={(e) => setSelectedTime(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="pm">
                                            PM
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    {formError && <div className="text-danger">{formError}</div>}
                    <button className="btn btn-submit w-100 mt-15px" onClick={handleSubmit}>
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Filters;